import {
  ResolvedEmployeeSummary,
  ResolvedEventSummary,
  ResolvedListSummary,
  ResolvedRecommendationSummary,
} from "@libry-content/common";
import { Documents } from "@libry-content/types";
import { usePathname } from "next/navigation";
import { z } from "zod";
import { logClick } from "../../utils/analytics/Plausible";
import { useMultipleUrlStates } from "../../utils/hooks/useMultipleUrlStates/useMultipleUrlStates";
import { ResolvedServiceSummary } from "@libry-content/common/src/services/groq/resolveService";

export type FilterableItem =
  | Documents
  | ResolvedEventSummary
  | ResolvedListSummary
  | ResolvedRecommendationSummary
  | ResolvedEmployeeSummary
  | ResolvedServiceSummary;

type GetFilterValuesFromItem<T extends FilterableItem> = (item: T) => string[];

export type FilterOption = {
  key: string;
  label?: string;
  value: string;
};

export const filterOptionsEqual =
  ({ key: keyA, value: valueA }: FilterOption) =>
  ({ key: keyB, value: valueB }: FilterOption): boolean =>
    keyA === keyB && valueA === valueB;

const isFilterOption = (potentialOption: unknown): potentialOption is FilterOption =>
  !!potentialOption && typeof potentialOption === "object" && "key" in potentialOption && "value" in potentialOption;

const uniqueFilterOptions = (options: FilterOption[]): FilterOption[] =>
  options.filter((option, index) => !options.slice(index + 1).find(filterOptionsEqual(option)));

// When checking which options in a filter are relevant, we need the items filtered by all other filters
export type FilteredByOtherFilters<T extends FilterableItem> = { [key: string]: T[] };

export type Filter<T extends FilterableItem> = {
  key: string;
  label?: string;
  selectedOptions: FilterOption[];
  optionIsSelected: (option: FilterOption) => boolean;
  onClickOption: (option: FilterOption) => void;
  filterPredicate: (item: T) => boolean;
  getRelevantOptions: (filteredItems: FilteredByOtherFilters<T>) => FilterOption[];
  isRelevant: (filteredItems: FilteredByOtherFilters<T>) => boolean;
};

const identity = <T>(item: T): T => item;

const useFilterState = (key: string) => {
  const [queryParameters, setQueryParameters] = useMultipleUrlStates(
    z.object({
      [key]: z.array(z.string()).optional(),
    })
  );

  const selectedOptions = queryParameters?.[key]?.map((value) => ({ key, value })) ?? [];

  const setSelectedOptions = (selectedOptions: FilterOption[]) => {
    setQueryParameters({
      [key]: !selectedOptions.length ? undefined : selectedOptions.map((filterOption) => filterOption.value),
    });
  };

  return [selectedOptions, setSelectedOptions] as const;
};

const useFilter = <T extends FilterableItem>(
  key: string,
  getFilterValuesFromItem: GetFilterValuesFromItem<T>,
  sortFilterOptions: (optionValues: FilterOption[]) => FilterOption[] = identity,
  label?: string
): Filter<T> => {
  const [selectedOptions, setSelectedOptions] = useFilterState(key);

  const pathname = usePathname();

  const optionsFromItems = (currentItems: T[] = []): FilterOption[] =>
    currentItems
      .map((item) => getFilterValuesFromItem(item)?.map((value) => ({ key, value })))
      .flat()
      .filter(isFilterOption);

  const getRelevantOptions = (filteredByOtherFilters: { [key: string]: T[] }): FilterOption[] =>
    sortFilterOptions(uniqueFilterOptions([...selectedOptions, ...optionsFromItems(filteredByOtherFilters[key])]));

  const isRelevant = (filteredByOtherFilters: { [key: string]: T[] }): boolean =>
    getRelevantOptions(filteredByOtherFilters).length > 1;

  const optionIsSelected = (option: FilterOption): boolean => !!selectedOptions.find(filterOptionsEqual(option));

  const onClickOption = (option: FilterOption) => {
    const alreadySelected = optionIsSelected(option);

    if (!alreadySelected) {
      const route = pathname?.split("/").reverse()?.[0];
      logClick(`FilterOption: /${route} – ${option.key}`, { filterOptionValue: option.value });
    }

    const newState = alreadySelected
      ? selectedOptions.filter((selectedOption) => !filterOptionsEqual(selectedOption)(option))
      : [...selectedOptions, option];

    setSelectedOptions(newState);
  };

  const filterPredicate = (item: T): boolean => {
    if (selectedOptions.length === 0) return true;
    const values = getFilterValuesFromItem(item);
    const selectedOptionValues = selectedOptions.map(({ value }) => value);
    return Array.isArray(values) && !!values.find((value) => value && selectedOptionValues.includes(value));
  };

  return {
    getRelevantOptions,
    isRelevant,
    selectedOptions,
    onClickOption,
    filterPredicate,
    optionIsSelected,
    key,
    label,
  };
};

export default useFilter;
